import React, { useState, useMemo } from 'react';
import { User, Phone, Shield, X, EyeIcon, EyeOffIcon, Home, Calendar, CheckCircle } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { Button, InputField, OTPInput } from '@shared/components';
import { useAppDarkMode } from '../hooks/useAppDarkMode';
import { useEmailVerification } from '../auth/hooks';
import { usePhoneVerification } from '../auth/hooks';
import PhoneVerificationModal from '../auth/components/PhoneVerificationModal';
import PasskeyManager from '../auth/components/PasskeyManager';
import AddressBook from '../components/profile/AddressBook';
import { useSelector } from 'react-redux';
import { getCourierSetting } from '../state/courierSlice';
import type { DeliveryPreference } from '../utils/deliveryPreference';

const Profile = () => {
  const { darkMode } = useAppDarkMode();
  const courierSetting = useSelector(getCourierSetting);
  const {
    user,
    formData,
    isLoading,
    error,
    storeLocations,
    handleFieldChange,
    handleAddressChange,
    handleAddAddress,
    handleRemoveAddress,
    handleSetPrimaryAddress,
    saveProfile,
    // Password change
    showChangePasswordModal,
    setShowChangePasswordModal,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleChangePassword,
    // Delete account
    showDeleteAccountModal,
    setShowDeleteAccountModal,
    deleteConfirmation,
    setDeleteConfirmation,
    handleDeleteAccount,
  } = useProfile();

  // OTP verification state
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [verificationField, setVerificationField] = useState<'email' | 'phone' | null>(null);
  const [pendingFormData, setPendingFormData] = useState(formData);
  
  // Phone verification modal state
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const { 
    sendEmailVerification, 
    verifyEmailCode, 
    isLoading: isEmailVerifying, 
    isSendingVerification: isSendingEmailVerification 
  } = useEmailVerification();
  
  const {
    sendPhoneVerification,
    verifyWhatsAppCode,
    isLoading: isPhoneVerifying,
    isSendingVerification: isSendingPhoneVerification
  } = usePhoneVerification();

  // Determine verification method (prioritize SMS)
  const verificationMethod: 'sms' | 'whatsapp' = useMemo(() => {
    if (courierSetting?.isSmsApiEnabled) {
      return 'sms';
    }
    return 'whatsapp';
  }, [courierSetting]);

  const handleSaveWithVerification = async () => {
    const needsVerification = 
      (formData.email !== user?.email) || 
      (formData.phone !== user?.contactInformation?.mobilePhone);

    if (needsVerification) {
      setPendingFormData(formData);
      // Determine which field changed (prioritize email if both changed)
      const changedField = formData.email !== user?.email ? 'email' : 'phone';
      setVerificationField(changedField);
      setShowOTPModal(true);
      
      // Send verification code
      if (changedField === 'email') {
        sendEmailVerification(user?.id?.toString() || '', formData.email);
      } else if (changedField === 'phone') {
        sendPhoneVerification(user?.id?.toString() || '', verificationMethod, formData.phone);
      }
    } else {
      saveProfile();
    }
  };

  const handleVerificationComplete = async (code: string) => {
    if (!user?.id) return;

    try {
      let verificationSuccess = false;
      
      if (verificationField === 'email') {
        const result = await verifyEmailCode(code, user.id.toString());
        verificationSuccess = result.success;
      } else if (verificationField === 'phone') {
        const result = await verifyWhatsAppCode(code, user.id.toString());
        verificationSuccess = result.success;
      }

      if (verificationSuccess) {
        try {
          const override = verificationField === 'email'
            ? { email: pendingFormData.email }
            : { phone: pendingFormData.phone };
          await saveProfile({ restrictTo: verificationField || undefined, override, force: true });
          setShowOTPModal(false);
        } catch (saveError) {
          console.error('Failed to save profile after verification:', saveError);
          // Keep the modal open if save fails
        }
      }
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pb-32">
      <div className="max-w-4xl mx-auto px-6 pt-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-8">
            <div className="h-16 w-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mr-4">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div>
              <h2 className="text-xl font-bold dark:text-white">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Member since {new Date(user?.createdOn || '').toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center mb-6">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Personal Information
              </h3>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <InputField
                    darkMode={darkMode}
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleFieldChange({ firstName: e.target.value })}
                    fullWidth
                  />
                </div>
                <div>
                  <InputField
                    darkMode={darkMode}
                    label="Last Name"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleFieldChange({ lastName: e.target.value })}
                    fullWidth
                  />
                </div>
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange({ email: e.target.value })}
                  fullWidth
                />
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="Phone"
                  type="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange({ phone: e.target.value })}
                  fullWidth
                />
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="Store"
                  type="select"
                  name="storeLocationId"
                  value={formData.storeLocationId}
                  onChange={(e) => handleFieldChange({ storeLocationId: parseInt(e.target.value) || undefined })}
                  options={storeLocations}
                  fullWidth
                />
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="Delivery preference"
                  type="select"
                  name="deliveryPreference"
                  value={formData.deliveryPreference}
                  onChange={(e) => {
                    const value = e.target.value as DeliveryPreference | '';
                    if (value) handleFieldChange({ deliveryPreference: value });
                  }}
                  options={[
                    { value: 'delivery', label: 'Deliver to me' },
                    { value: 'pickup', label: "I'll pick up at the store" },
                    { value: 'call_first', label: 'Call me first' },
                  ]}
                  fullWidth
                />
                <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  "Call me first" means we deliver, but phone you before heading out.
                </p>
              </div>

            </div>
          </div>

          {/* Address Information */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <Home className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Address Information
              </h3>
            </div>

            <AddressBook
              addresses={formData.addresses}
              darkMode={darkMode}
              onAddressChange={handleAddressChange}
              onAddAddress={handleAddAddress}
              onRemoveAddress={handleRemoveAddress}
              onSetPrimaryAddress={handleSetPrimaryAddress}
            />
          </div>

          {/* Additional Information */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <Calendar className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Additional Information
              </h3>
            </div>

            <div className="space-y-6">
              <div>
                <InputField
                  darkMode={darkMode}
                  label="Gender"
                  type="select"
                  name="gender"
                  value={formData.gender}
                  onChange={(e) => handleFieldChange({ gender: e.target.value })}
                  options={[
                    { value: '', label: 'Select gender' },
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
                  ]}
                  fullWidth
                />
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="Date of Birth"
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleFieldChange({ dateOfBirth: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  fullWidth
                />
              </div>

              <div>
                <InputField
                  darkMode={darkMode}
                  label="TRN (Tax Registration Number)"
                  type="text"
                  name="trnNumber"
                  value={formData.trnNumber}
                  onChange={(e) => handleFieldChange({ trnNumber: e.target.value })}
                  fullWidth
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div>
            <Button
              onClick={handleSaveWithVerification}
              disabled={isLoading}
              isLoading={isLoading}
              variant="primary"
              fullWidth
            >
              Save Changes
            </Button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-6">
            <Shield className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              Security
            </h3>
          </div>

          <div className="space-y-3">
            {/* Phone Verification Status/Button */}
            {!user?.phoneVerified ? (
              <Button
                variant="primary"
                fullWidth
                onClick={() => setShowPhoneVerificationModal(true)}
                className="flex items-center justify-center space-x-2"
              >
                <Phone className="h-4 w-4" />
                <span>Verify Phone Number</span>
              </Button>
            ) : (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-green-800 dark:text-green-200 font-medium">
                    Phone Verified
                  </span>
                </div>
              </div>
            )}
            
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setShowChangePasswordModal(true)}
            >
              Change Password
            </Button>

            {/* Renders nothing when the courier has passkeys switched off. */}
            <PasskeyManager />

            <Button
              variant="danger"
              fullWidth
              onClick={() => setShowDeleteAccountModal(true)}
            >
              Delete Account
            </Button>
          </div>
        </div>

        {/* Change Password Modal */}
        {showChangePasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium dark:text-white">
                  Change Password
                </h3>
                <button
                  onClick={() => setShowChangePasswordModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                                      <InputField
                    darkMode={darkMode}
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    fullWidth
                  />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showCurrentPassword ? (
                        <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                                      <InputField
                    darkMode={darkMode}
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    fullWidth
                  />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showNewPassword ? (
                        <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                                      <InputField
                    darkMode={darkMode}
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    fullWidth
                  />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="secondary"
                    onClick={() => setShowChangePasswordModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleChangePassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                    isLoading={isLoading}
                  >
                    Update Password
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-red-600 dark:text-red-400">
                  Delete Account
                </h3>
                <button
                  onClick={() => setShowDeleteAccountModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md">
                  <p className="text-red-600 dark:text-red-400 font-medium">
                    Warning: This action cannot be undone
                  </p>
                  <p className="text-red-600 dark:text-red-400 mt-1">
                    Deleting your account will permanently remove all your data,
                    including your profile, packages, and invoices.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type "DELETE" to confirm
                  </label>
                  <InputField
                    darkMode={darkMode}
                    type="text"
                    name="deleteConfirmation"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="DELETE"
                    fullWidth
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteAccountModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmation !== 'DELETE'}
                    isLoading={isLoading}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* OTP Verification Modal */}
        {showOTPModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium dark:text-white">
                  Verify {verificationField === 'email' ? 'Email' : 'Phone Number'}
                </h3>
                <button
                  onClick={() => {
                    setShowOTPModal(false);
                    setVerificationField(null);
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <OTPInput
                onComplete={handleVerificationComplete}
                onResend={() => {
                  if (verificationField === 'email') {
                    sendEmailVerification(user?.id?.toString() || '', pendingFormData.email);
                  } else {
                    sendPhoneVerification(user?.id?.toString() || '', verificationMethod, pendingFormData.phone);
                  }
                }}
                title={`Verify Your ${verificationField === 'email' ? 'Email' : 'Phone Number'}`}
                description={`We've sent a verification code to ${
                  verificationField === 'email' 
                    ? pendingFormData.email 
                    : pendingFormData.phone
                }. Please enter it below.`}
                isLoading={verificationField === 'email' ? isEmailVerifying : isPhoneVerifying}
                disabled={verificationField === 'email' ? isSendingEmailVerification : isSendingPhoneVerification}
                isResending={verificationField === 'email' ? isSendingEmailVerification : isSendingPhoneVerification}
              />
            </div>
          </div>
        )}

        {/* Phone Verification Modal */}
        <PhoneVerificationModal
          isOpen={showPhoneVerificationModal}
          onClose={() => setShowPhoneVerificationModal(false)}
        />
      </div>
    </div>
  );
};

export default Profile;